import React, { useEffect, useMemo } from 'react'
import { View, StyleSheet, TouchableOpacity } from 'react-native'
import { Text } from 'react-native'
import { BtScreen, BtText } from '@linqpal/components/src/ui'
import BuilderBottomModal from '../molecules/BuilderBottomModal'
import { DateWillBeFinalized, PaymentPlanCard } from '../atoms/PaymentPlanCard'
import { Spacer } from '../atoms'
import { useInvoiceDetailsContext } from '../../screens/Contractor/TabInvoice/InvoiceDetailsContext'
import { BButton } from '../atoms/builder-2.0/Button'
import { useStore } from '../../store'
import { useResponsive } from '../../utils/hooks'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import ApplicationStore from '../../screens/GeneralApplication/Application/ApplicationStore'
import { observer } from 'mobx-react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigation } from '@react-navigation/core'
import { composeStyle } from '../../../../components/src/helpers'
import { IconCloseSquare } from '../../assets/icons'
import { paths } from '../../screens/links'
import { frequency } from '@linqpal/models/src/dictionaries'
import Loading from '../../screens/Loading'
import _ from 'lodash'
import { toJS } from 'mobx'
import { PayInvoiceWithCreditModal } from '../../screens/Contractor/InvoicesTab/PayInvoice/PayInvoiceModal'
import { PricingProduct } from '@linqpal/models/src/dictionaries/pricingProduct'

export const PaymentPlanSelectBottomModal = observer((props) => {
  const { onClose, invoices } = props
  const {
    paymentPlanModalVisibility,
    setPaymentPlanModalVisibility,
    payInvoiceModal,
    setPayInvoiceModal,
  } = useInvoiceDetailsContext()
  const { t } = useTranslation('application')
  const { paymentPlans, selectedPaymentPlan } = ApplicationStore

  const subtitle = useMemo(() => {
    const onePay = paymentPlans.find((p) => p.frequency === frequency.SINGLE)
    if (onePay || !paymentPlans.length) {
      return null
    } else {
      return (
        <Trans
          t={t}
          i18nKey="CreditRequestPaymentPlan.NoPaymentDueFirst30days"
          components={{
            styled: (
              <Text
                style={{ color: commonColors.accentText, fontWeight: '500' }}
              />
            ),
            simple: (
              <Text
                style={{ fontWeight: '500', fontSize: 12, color: '#668598' }}
              />
            ),
          }}
        />
      )
    }
  }, [paymentPlans, t])

  return (
    <>
      <BuilderBottomModal
        visible={paymentPlanModalVisibility}
        onClose={() => {
          onClose && onClose()
          setPaymentPlanModalVisibility(false)
        }}
        title={t('CreditRequestPaymentPlan.mobileTitle')}
        subtitle={subtitle}
        // eslint-disable-next-line i18next/no-literal-string
        backgroundColor="white"
        footer={<Footer invoices={invoices} />}
      >
        <Content {...props} />
      </BuilderBottomModal>
      {payInvoiceModal && !!selectedPaymentPlan && (
        <PayInvoiceWithCreditModal
          modal={payInvoiceModal}
          setModal={setPayInvoiceModal}
          invoiceIds={invoices.map((i) => i._id)}
        />
      )}
    </>
  )
})

const Footer = observer(({ invoices }) => {
  const { t } = useTranslation('global')
  const invoice = invoices[0]
  const {
    loadingPlans,
    paymentPlans,
    selectedPaymentPlan,
    selectPlanAndContinue,
  } = ApplicationStore
  const { setPaymentPlanModalVisibility, setPayInvoiceModal } =
    useInvoiceDetailsContext()
  const {
    userStore: { document },
  } = useStore()
  const uploadedInvoiceStyle = {
    view: styles.customView,
    text: styles.customText,
  }
  const regularInvoiceStyle = { view: styles.customView2 }
  const onContinueSuccess = () => {
    setPaymentPlanModalVisibility(false)
    setPayInvoiceModal(true)
  }

  const onContinue = () => {
    selectPlanAndContinue(
      document?.loanApplicationId,
      onContinueSuccess,
      invoices,
    )
  }

  return (
    <>
      {!paymentPlans.length && loadingPlans ? (
        <View style={styles.loadingView}>
          <Loading style={styles.loading}>
            <Text style={styles.loadingText}>
              {t('CreditApplication.LoadingPlans')}
            </Text>
          </Loading>
        </View>
      ) : (
        <>
          <Spacer height={15} />
          <DateWillBeFinalized
            customStyle={
              invoice?.supplierInvitationDetails
                ? uploadedInvoiceStyle
                : regularInvoiceStyle
            }
            showInfo={false}
          />
          <View
            style={{
              backgroundColor: '#FFF',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Spacer height={12} />
            <View style={{ width: 335 }}>
              <BButton
                onPress={onContinue}
                buttonStyle={styles.continueButton}
                loading={loadingPlans}
                disabled={loadingPlans || !selectedPaymentPlan}
              >
                {t('CreditApplication.Continue')}
              </BButton>
            </View>
            <Spacer height={45} />
          </View>
        </>
      )}
    </>
  )
})

export const Content = observer(({ invoices }) => {
  let totalAmount = 0
  const invoice = invoices[0]
  for (const inv of invoices) {
    totalAmount += inv.total_amount
  }
  const {
    setSelectedPaymentPlan,
    selectedPaymentPlan,
    paymentPlans,
    getPaymentPlans,
  } = ApplicationStore

  useEffect(() => {
    getPaymentPlans(invoice?._id, totalAmount)
    setSelectedPaymentPlan(null)
  }, [invoice?._id, getPaymentPlans, setSelectedPaymentPlan, totalAmount])

  return (
    <View style={{ width: '100%' }}>
      {!!paymentPlans.length && <Spacer height={12} />}
      {paymentPlans.map(
        (paymentPlan, index) =>
          paymentPlan.product === PricingProduct.LineOfCredit && (
            <View key={index}>
              <PaymentPlanCard
                onPress={() => {
                  setSelectedPaymentPlan(toJS(paymentPlan))
                }}
                paymentOption={paymentPlan}
                selected={selectedPaymentPlan?._id === paymentPlan._id}
                paymentAmount={totalAmount}
                forSummaryPage={undefined}
              />
              <Spacer height={16} />
            </View>
          ),
      )}
    </View>
  )
})

export const PlanSelection = () => {
  const { sm } = useResponsive()
  const { t } = useTranslation('application')
  const navigation = useNavigation()
  const { invoiceDetails } = ApplicationStore
  const { setPaymentPlanModalVisibility } = useInvoiceDetailsContext()

  if (sm) {
    return (
      <BtScreen
        title={t('CreditRequestPaymentPlan.title')}
        titleCentered
        rightTitleAction={
          <TouchableOpacity
            style={composeStyle(
              {
                borderColor: '#E6EBEE',
                borderRadius: 6,
                borderWidth: 1,
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
              },
              !sm && {
                borderWidth: 0,
                borderRadius: 8,
                backgroundColor: '#F5F7F8',
              },
            )}
            onPress={() => {
              setPaymentPlanModalVisibility(false)
              navigation.navigate(paths.Console._self)
            }}
          >
            <IconCloseSquare width={12} height={12} />
          </TouchableOpacity>
        }
        leftTitleAction={<View style={{ width: 20 }} />}
      >
        <View style={{ width: 335, alignSelf: 'center', alignItems: 'center' }}>
          <BtText style={styles.heading}>
            {t('ChooseAPaymentPlan.Heading')}
          </BtText>
          <BtText style={styles.subHeading}>
            {t('ChooseAPaymentPlan.SubHeading')}
          </BtText>
          <Content invoices={invoiceDetails} />
          <Footer invoices={invoiceDetails} />
        </View>
      </BtScreen>
    )
  } else {
    return <PaymentPlanSelectBottomModal invoices={invoiceDetails} />
  }
}

const styles = StyleSheet.create({
  heading: {
    marginTop: 40,
    fontWeight: '600',
    fontSize: 16,
    lineHeight: 24,
    alignSelf: 'center',
    color: '#003353',
  },
  subHeading: {
    marginTop: 4,
    marginBottom: 9,
    fontWeight: '500',
    fontSize: 12,
    lineHeight: 20,
    color: '#335C75',
    width: 250,
    textAlign: 'center',
  },
  customView: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  customText: {
    marginLeft: 0,
  },
  customView2: {
    alignSelf: 'center',
  },
  loading: {
    flexDirection: 'row',
  },
  loadingText: {
    marginLeft: 20,
    fontWeight: '500',
    color: '#335C75',
  },
  loadingView: {
    minHeight: 250,
  },
  continueButton: {
    width: '100%',
    alignSelf: 'center',
  },
})
